import { useDisclosure } from '@heroui/react'
import {
  InfiniteData,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { format } from 'date-fns'

import { useApi, useApiClient } from '@/trpc/react'
import { InfiniteMomentsOutput } from '@/trpc/types'

import { useDecrypt } from '..'

interface UseDetailMomentArgs {
  momentId: string
  onDeleteSuccess: () => void
}

const useDetailMoment = ({
  momentId,
  onDeleteSuccess,
}: UseDetailMomentArgs) => {
  const api = useApi()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const {
    isOpen: isOpenAddTagModal,
    onOpenChange: onOpenChangeAddTagModal,
    onOpen: onOpenAddTagModal,
  } = useDisclosure()
  const {
    isOpen: isOpenCancelModal,
    onOpenChange: onOpenChangeCancelModal,
    onOpen: onOpenCancelModal,
  } = useDisclosure()

  const {
    data: moment,
    isLoading: isLoadingMoment,
    isPending: isPendingMoment,
  } = useSuspenseQuery(
    api.moments.getById.queryOptions({ id: momentId }, { enabled: !!momentId }),
  )

  const { data: decryptedContent = '', isLoading: isDecryptingContent } =
    useDecrypt({
      ciphertext: moment?.momentEncrypted ?? '',
    })
  const { data: decryptedTitle = '', isLoading: isDecryptingTitle } =
    useDecrypt({
      ciphertext: moment?.title ?? '',
    })

  const {
    mutate: updateTags,
    variables: updateTagsVariables,
    isPending: isPendingUpdateTags,
  } = useMutation(
    api.moments.updateTags.mutationOptions({
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries({
          queryKey: api.moments.getById.queryKey({ id: variables.id }),
        })
      },
    }),
  )

  const {
    mutate: updateFavorite,
    variables: updateFavoriteVariables,
    isPending: isPendingUpdateFavorite,
  } = useMutation(
    api.moments.updateFavorite.mutationOptions({
      onMutate: async (variables) => {
        const queryKey = api.moments.getById.queryKey({ id: variables.id })
        await queryClient.cancelQueries({
          queryKey,
        })
        const previousMoment = queryClient.getQueryData(queryKey)
        queryClient.setQueryData(queryKey, (old) => {
          if (!old) return old
          return { ...old, isFavorite: variables.isFavorite }
        })
        return { previousMoment, queryKey }
      },
      onError: (_error, _variables, context) => {
        if (context?.previousMoment) {
          queryClient.setQueryData(context.queryKey, context.previousMoment)
        }
      },
      onSettled: (_data, _error, _variables, context) => {
        if (context?.queryKey) {
          queryClient.invalidateQueries({
            queryKey: context.queryKey,
          })
          queryClient.invalidateQueries({
            queryKey: api.moments.getFavorites.queryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: api.moments.infiniteMoments.infiniteQueryKey(),
          })
        }
      },
    }),
  )

  const { mutate: deleteMoment } = useMutation(
    api.moments.delete.mutationOptions({
      onMutate: async (variables) => {
        const queryKey = api.moments.infiniteMoments.infiniteQueryKey({
          limit: 10,
        })
        await queryClient.cancelQueries({
          queryKey,
        })
        const previousMoments = queryClient.getQueryData(queryKey)
        queryClient.setQueryData<InfiniteData<InfiniteMomentsOutput>>(
          queryKey,
          (old) => {
            if (!old) return old
            return {
              ...old,
              pages: old.pages.map((page) =>
                page.filter((moment) => moment.id !== variables.id),
              ),
            }
          },
        )

        return {
          previousMoments,
          queryKey,
        }
      },
      onError: (error, _variables, context) => {
        console.error('Error deleting moment:', error)
        if (context?.previousMoments) {
          queryClient.setQueryData(context.queryKey, context.previousMoments)
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: api.moments.pathKey(),
        })
      },
    }),
  )

  const removeTag = (tag: string) => {
    const tags = moment?.tags as string[]
    if (!moment?.id) return
    updateTags({
      id: moment.id,
      tags: tags?.filter((t) => t !== tag) ?? [],
    })
  }
  const addTag = (tag: string) => {
    const tags = moment?.tags as string[]
    if (!moment?.id) return
    updateTags({
      id: moment.id,
      tags: [...tags, tag],
    })
  }
  const handleToggleFavorite = () => {
    if (!moment) return
    updateFavorite({
      id: moment.id,
      isFavorite: !moment.isFavorite,
    })
  }

  const formattedDate = moment?.date
    ? format(new Date(moment.date), 'do MMM')
    : ''

  const handleDeleteMoment = () => {
    if (!moment?.id) return
    deleteMoment({
      id: moment.id,
      images: moment.momentImages.flatMap((image) => [
        image.original,
        image.thumbnail,
      ]),
    })
    onDeleteSuccess()
  }

  const isLetter = moment?.isLetter
  const letterFormattedDate = moment?.createdAt
    ? format(new Date(moment.createdAt), 'do MMM, yyyy')
    : ''

  return {
    moment: {
      ...moment,
      tags: isPendingUpdateTags ? updateTagsVariables?.tags : moment?.tags,
    },
    isLetter,
    isLoading:
      isPendingMoment ||
      isLoadingMoment ||
      isDecryptingContent ||
      isDecryptingTitle,
    loaders: {
      isDecryptingContent,
      isDecryptingTitle,
      isLoadingMoment,
    },
    formattedContent: {
      decryptedContent,
      decryptedTitle,
      formattedDate,
      letterFormattedDate,
    },
    tagModal: {
      isOpen: isOpenAddTagModal,
      onOpenChange: onOpenChangeAddTagModal,
      onOpen: onOpenAddTagModal,
    },
    cancelModal: {
      isOpen: isOpenCancelModal,
      onOpenChange: onOpenChangeCancelModal,
      onOpen: onOpenCancelModal,
    },
    actions: {
      removeTag,
      addTag,
      handleToggleFavorite,
      handleDeleteMoment,
    },
  }
}

export default useDetailMoment
